import React, { useState, useEffect, createContext } from "react";
import { get } from 'lodash';

import { withSession } from '../session';
import {
  useApplicationState, upsertToasts, updateDomainDropdownList, updateIsApiContext
} from "../../utilities/applicationState";
import AddApisAndEventSubs from "./addApisAndEventSubs";
import getDevPortalContext from "../../utilities/devPortalContext";
import { setDropdownDefaults } from "./apiDiscoveryWizard";

const ApiPickerContext = createContext();

const ApiPicker = ({
  children, projectDetails, isApiTab, session, apiPickerFetchProjectDetails,
  disabled, clientOOID, carDetails, ...rest
}) => {
  const [applicationState, dispatch] = useApplicationState();

  const [visible, setVisible] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(0);
  // const [hideWizardPreference, setHideWizardPreference] = useState(true);
  // Wizard intro page is removed in this commit. If needed again, changes can be tracked and reverted back.
  const [isDomainSubCategoryBusy, setIsDomainSubCategoryBusy] = useState(false);
  const [disableAddApisBtn, setDisableAddApisBtn] = useState(true);

  const domainData = get(applicationState, 'apiDiscovery.domainDropdownList', []);
  const productCode = get(projectDetails, 'product.productCode', '');
  const { isApiContext } = applicationState.apiDiscovery;

  const { ooid, aoid } = session;
  const { b2bScopes = [], consentTexts = [] } = carDetails || {};

  const showErrorModal = (message) => dispatch(upsertToasts({
    message,
    status: "error",
    autoCloseAfter: 30000
  }));

  const openFocusPane = (open) => {
		if (open) {
			setDropdownDefaults(dispatch, domainData, isApiContext);
		}
    if (!isDomainSubCategoryBusy) {
      setVisible(open);
    }
	};

  // initial setup of data after API response
  const mapData = (data) => {
    const domainList = []

    const selections = get(data, 'domainSubCategories', [])

    selections.forEach(selection => {
      const domain = selection.domain;
      const subCategories = selection.subCategories.map((subCategory) => {
        return {
          domain,
          label: subCategory,
          value: subCategory
        };
      });

      const domainItemData = {
        label: domain,
        value: domain,
        disabled: false,
        subCategories
      };

      domainList.push(domainItemData);
    });
    dispatch(updateDomainDropdownList(domainList));
  }

  const fetchDomainSubCategoryData = async () => {
    try {
      setIsDomainSubCategoryBusy(true);

      const { data } = await getDevPortalContext().get(`${import.meta.env.VITE_API_V2_PATH}/${productCode}/domain-subcategories`);
      mapData(data);
      setDisableAddApisBtn(false);

    } catch (e) {
      setDisableAddApisBtn(true);
      const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching domain and sub-categories. Please try again.');
      showErrorModal(errorMessage);
    } finally {
      setIsDomainSubCategoryBusy(false)
    }
  };

  useEffect(() => {
    if(isApiTab && productCode && !disabled) {
      fetchDomainSubCategoryData();
    }
  }, [isApiTab, productCode, disabled])

  useEffect(() => {
    if(isApiTab && !disabled) {
      setSelectedSegment(0);
    }
  }, [isApiTab, disabled])

  useEffect(() => {
    if (!disabled) dispatch(updateIsApiContext(selectedSegment === 0));
  }, [selectedSegment, disabled]);

  if (disabled) {
    return children;
  }

  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        domainData, setSelectedSegment, isDomainSubCategoryBusy, disableAddApisBtn, openFocusPane,
        isDomainSubCategoryBusyApiCentral: isDomainSubCategoryBusy,
        disableApiCentralAddApisBtn: disableAddApisBtn
      });
    }
    return child;
  })

  return (
    <ApiPickerContext.Provider
      value={{ projectDetails, apiPickerFetchProjectDetails, isApiContext, clientOOID, b2bScopes, consentTexts }}
    >
      <AddApisAndEventSubs
        setVisible={openFocusPane}
        projectIdEncrypted={get(projectDetails, 'clientProjectDetails._id', '') || get(projectDetails, '_id', '')}
        consumerApplicationID={get(projectDetails, 'clientProjectDetails.b2bConsumerAppID', '') || get(projectDetails, 'b2bConsumerAppID', '') }
        {...{
          ooid, aoid, isApiContext, openFocusPane, visible, productCode, ...rest
        }}
      />
      {childrenWithProps}
    </ApiPickerContext.Provider>
  );
};

export default withSession(ApiPicker);
export { ApiPickerContext };